import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { trpc } from '@/trpc'
import { useDashboard } from '@/context/DashboardContext'
import { GateLayout } from '@/components/auth/GateLayout'
import { AlertTriangle, LifeBuoy } from '@/components/icons'
import { Button } from '@/components/ui'
import { PendingDeletionNotice, readPendingDeletion } from '@/components/PendingDeletionNotice'
import type { PendingDeletion } from '@/components/PendingDeletionNotice'
import { getSafeReturnPath } from '@/lib/auth/returnPath'
import { centreOf, revealFrom, shellReady } from '@/lib/theme/viewTransition'

export function MagicLoginPage() {
  const { tenant } = useParams<{ tenant: string }>()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  // Where the sign-in started, carried by the texted link (an upgrade link goes back to billing).
  const returnPath = getSafeReturnPath(searchParams.get('next'))
  const navigate = useNavigate()
  const { dispatch } = useDashboard()
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const [pendingDeletion, setPendingDeletion] = useState<PendingDeletion | null>(null)
  const redemptionStarted = useRef(false)
  const anchorRef = useRef<HTMLDivElement>(null)
  const utils = trpc.useUtils()

  const magicLogin = trpc.auth.magicLogin.useMutation({
    // Into the dashboard through an iris on the mark; the browser holds this
    // page on screen while the identity changes underneath.
    onSuccess: (data) => revealFrom('iris', centreOf(anchorRef.current), () => {
      // Cancel outgoing account queries before the new identity can mount tenant pages.
      queryClient.clear()
      // Set auth state directly from the response so ProtectedRoute
      // sees isAuthenticated=true immediately (no me-query race).
      dispatch({
        type: 'SET_USER',
        payload: {
          name: data.user.name,
          email: data.user.email,
          hasSetPassword: !data.needsPasswordSetup,
          badclawTenant: data.user.badclawTenant
            ? {
                tenantId: data.user.badclawTenant.tenantId,
                containerPort: data.user.badclawTenant.containerPort ?? 0,
                subdomain: data.user.badclawTenant.subdomain ?? '',
                provisionedAt: String(data.user.badclawTenant.provisionedAt ?? ''),
              }
            : undefined,
        },
      })
      navigate(returnPath, { replace: true })
      return shellReady()
    }),
    onError: (mutationError) => {
      // The link worked, but the account is inside its 30-day deletion window.
      const pending = readPendingDeletion(mutationError)
      if (pending) {
        setPendingDeletion(pending)
        return
      }
      setError(mutationError.data?.code === 'UNAUTHORIZED' || mutationError.data?.code === 'BAD_REQUEST'
        ? 'This link is invalid, expired, or has already been used.'
        : 'cause' in mutationError && mutationError.cause instanceof TypeError
          ? 'Could not reach the server. Check your connection and request a new sign-in link.'
          : mutationError.message || 'Sign-in could not finish. Please try again.')
    },
  })

  useEffect(() => {
    if (redemptionStarted.current) return
    redemptionStarted.current = true

    if (!token || !tenant) {
      setError('No token provided')
      return
    }

    // Remove the bearer credential before any network request so browser
    // history, screenshots, referrers, and reloads do not retain it.
    const cleanUrl = new URL(window.location.href)
    cleanUrl.searchParams.delete('token')
    window.history.replaceState(null, '', `${cleanUrl.pathname}${cleanUrl.search}${cleanUrl.hash}`)

    magicLogin.mutate({ token, tenantId: tenant })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant, token])

  const title = pendingDeletion || error
    ? <>Back to<br /><span className="text-[color:var(--bl-gold)]">camp.</span></>
    : <>Lighting<br />your <span className="text-[color:var(--bl-gold)]">fire.</span></>

  return (
    <GateLayout kicker="Agent dashboard" title={title} anchorRef={anchorRef}>
      {pendingDeletion ? (
        <PendingDeletionNotice
          pending={pendingDeletion}
          onRestored={() => {
            void utils.auth.me.invalidate().then(() => navigate(returnPath, { replace: true }))
          }}
          onDismiss={() => navigate('/login', { replace: true })}
        />
      ) : error ? (
        <div>
          <div className="mb-5 flex items-start gap-3">
            <span aria-hidden="true" className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[rgb(var(--color-warning)/0.12)] text-[rgb(var(--color-text-warning))] ring-1 ring-[rgb(var(--color-warning)/0.3)]">
              <AlertTriangle size={18} />
            </span>
            <div className="min-w-0">
              <h2 className="font-[family-name:var(--bl-font-display)] text-2xl font-bold uppercase tracking-[0.03em] text-[color:var(--bl-ink)]">
                Sign-in unsuccessful
              </h2>
              <p className="mt-1 text-sm leading-relaxed text-[color:var(--bl-ink-2)]">{error}</p>
            </div>
          </div>
          <div className="space-y-3">
            <Button fullWidth size="lg" onClick={() => navigate('/login?method=agent-link', { replace: true })}>
              Send another link
            </Button>
            <Button fullWidth size="lg" variant="secondary" onClick={() => navigate('/login', { replace: true })}>
              Sign in another way
            </Button>
          </div>
          <a
            href="mailto:support@badland.ai?subject=Dashboard%20login%20help"
            className="mx-auto mt-5 flex w-fit items-center gap-1.5 text-xs font-medium text-[color:var(--bl-ink-3)] transition-colors hover:text-[color:var(--bl-gold-text)]"
          >
            <LifeBuoy size={13} aria-hidden="true" />
            Contact support
          </a>
        </div>
      ) : (
        <div className="flex items-center gap-4 py-1" aria-live="polite">
          <span aria-hidden="true" className="bl-ember-spinner h-6! w-6! shrink-0" />
          <div className="min-w-0">
            <p className="text-sm text-[color:var(--bl-ink-2)]">
              Signing in to <span className="font-[family-name:var(--bl-font-mono)] text-[color:var(--bl-ink)]">{tenant}</span>...
            </p>
            <p className="mt-0.5 text-xs text-[color:var(--bl-ink-3)]">Single-use link. It stops working once it is used.</p>
          </div>
        </div>
      )}
    </GateLayout>
  )
}
